/**
 * 목업 영상 → AI 동영상 — 콘텐츠에 첨부한 목업 mp4 를 "이런 느낌으로" 삼아 Veo 로 실제 영상을 만들어 다시 첨부한다.
 *
 * Veo 는 아무 mp4 나 입력으로 받지 않는다 (영상 입력은 Veo 가 만든 영상의 연장뿐). 그래서:
 *   1) 분석 — 목업에서 일정 간격으로 프레임을 뽑아 Gemini 에게 보여 주고, 컷마다 장면 계획(시간·설명·방식)을 받는다 (erp-video mockup_plan)
 *   1-1) 첫 화면 고치기(선택) — 장면 시작 화면을 구도·앵글은 그대로 두고 요청사항·참고 사진대로 고친다 (erp-video image_edit, 장당 ≈$0.04)
 *              예: "배경을 사그라다 파밀리아로" + 사그라다 파밀리아 사진. Veo 는 첫 프레임에 보이는 것에서 출발하므로 배경을 바꾸려면 글이 아니라 이 화면을 바꿔야 한다.
 *   2) 생성 — 장면마다 목업의 그 시작 화면(고쳤으면 고친 화면)을 첫 프레임으로 Veo 이미지→영상 클립을 만든다 (유료, 장면당 1~3분)
 *              목업 화면이 스케치·글자 슬라이드라 첫 프레임으로 못 쓰면 설명만으로 새로 그린다(story)
 *   3) 합치기 — 클립을 목업의 장면 길이만큼 잘라 순서대로 이어 붙인다 (introVideoBuilder.concatClips, 브라우저 안에서)
 *   4) 첨부 — 완성 mp4 를 같은 콘텐츠의 첨부파일로 올린다. 콘텐츠 저장은 편집 화면의 저장 버튼이 한다.
 * 목업의 자막·로고 글자는 Veo 가 제대로 못 그려서 영상에 넣지 않는다 (장면마다 overlayText 로 알려만 준다).
 *
 * 10초 이하 목업은 위 과정 대신 Gemini Omni Flash 로 바로 만든다 (engine 'omni'):
 *   목업 mp4 를 작게 다시 인코딩해 렌더링 사진·요청과 함께 그대로 넘긴다 — 목업 = 카메라·구도·타이밍, 사진 = 내용.
 *   Veo 와 달리 Omni 는 영상을 입력으로 받는다(편집 입력 10초 이하). 결과를 마무리(소리·크기 맞추기)해서 첨부한다.
 *
 * 렌더링 사진 모드(분석 전에 사진을 넣었을 때, plan.refMode): 역할을 나눈다.
 *   목업 영상 = 카메라 안내(샷 크기·앵글·움직임·구도·컷 타이밍)만 / 렌더링 사진 = 실제로 화면에 그릴 내용.
 *   분석이 장면마다 쓸 사진(refs)을 고르고, 첫 화면은 "그 사진의 내용을 목업 구도로" 새로 그린다(image_edit role:'compose').
 *   목업 화면 자체는 Veo 에 가지 않는다. 영상 만들기를 누르면 아직 안 만든 첫 화면은 자동으로 먼저 만든다.
 */
import React, { useEffect, useRef, useState } from 'react';
import { requestMockupPlan, generateClip, editImage, generateOmni, OMNI_MAX_INPUT_SEC, OMNI_USD_PER_SEC_ESTIMATE, IMAGE_USD_PER_IMAGE, VEO_TIERS, veoUsdPerSec, summarizeUsage, usageLog, resetUsageLog } from '../services/ai/videoClient';
import { isAiConfigured } from '../services/ai/geminiClient';
import { grabFrames, concatClips, isVideoBuildSupported, makeFirstFrameBase64, makeThumbnailBase64, MAX_OUTPUT_BYTES } from '../services/video/introVideoBuilder';
import { clipCacheKey, getCachedClip, putCachedClip } from '../services/video/clipCache';
import { uploadContentFile } from '../services/apiService';
import { StepChip, Field, Notice, Spinner } from './AiIntroVideoModal';

/** 서버(erp-video)의 MAX_MOCKUP_SEC 와 같은 값 */
const MAX_MOCKUP_SEC = 64;
/** 분석에 보낼 프레임 수 (서버 상한 24) */
const ANALYZE_FRAMES = 20;
const MODE_LABELS = { photo: '화면 그대로 움직임', action: '그 장소에서 행동', story: '새로 그림' };
const AUDIO_OPTIONS = [
  { id: 'mockup', label: '목업 영상의 소리' },
  { id: 'clips', label: 'AI 현장음' },
  { id: 'none', label: '무음' },
];

/** 참고 사진 수 (서버 상한 3) */
const MAX_REFERENCES = 3;

/** 클립 캐시 키용 짧은 지문 — 고친 첫 화면이 바뀌면 다른 클립으로 취급하려고 (긴 base64 를 띄엄띄엄 FNV-1a) */
function fingerprint(str) {
  let h = 2166136261;
  const step = Math.max(1, Math.floor(str.length / 4096));
  for (let i = 0; i < str.length; i += step) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
  return `${(h >>> 0).toString(36)}${str.length}`;
}

const IMAGE_EXT_RE = /\.(jpe?g|png|webp|gif|bmp)$/i;

/** 내 컴퓨터의 사진 파일 → 긴 변 size px JPEG base64. fetch(blob:) 를 거치지 않고 파일을 바로 디코딩한다. 실패하면 null. */
async function fileToJpegBase64(file, size = 1024) {
  let img = null;
  try {
    img = await createImageBitmap(file);
  } catch {
    // createImageBitmap 이 못 여는 형식이면 <img> 로 한 번 더 (그래도 안 되면 HEIC 등 브라우저가 모르는 형식)
    const url = URL.createObjectURL(file);
    img = await new Promise((resolve) => { const el = new Image(); el.onload = () => resolve(el); el.onerror = () => resolve(null); el.src = url; });
    URL.revokeObjectURL(url);
  }
  if (!img) return null;
  const iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
  if (!iw || !ih) return null;
  const scale = Math.min(1, size / Math.max(iw, ih));
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(iw * scale)); c.height = Math.max(1, Math.round(ih * scale));
  c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
  const dataUrl = c.toDataURL('image/jpeg', 0.8);
  return dataUrl.slice(dataUrl.indexOf(',') + 1);
}

/** 장면이 Veo 첫 프레임으로 쓸 화면 — 고친 화면을 쓰기로 했으면 그것, 아니면 목업 원본 */
const frameOf = (s) => (s.useEdited && s.edited ? s.edited : s.image);

/** Veo 길이(4/6/8초) 중 장면 길이를 덮는 가장 짧은 것 */
const veoSecondsFor = (len) => (len <= 4 ? 4 : len <= 6 ? 6 : 8);

/** Omni 에 보낼 목업의 첫 시도 영상 비트레이트 — 10초 ≈ 1.9MB */
const OMNI_UPLOAD_BITRATE = 1_500_000;
/** 목업 mp4 상한 (base64 로 서버의 MAX_OMNI_VIDEO_CHARS 3M자 안) */
const OMNI_UPLOAD_MAX_BYTES = 2_200_000;
/** erp-video 요청 본문 상한(4MB)에서 JSON 껍데기·지시문 몫을 뺀 것. 목업 + 사진이 함께 이 안에 들어가야 한다 */
const OMNI_BODY_BUDGET_CHARS = 4_194_304 - 80_000;

function blobToBase64(b) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => { const u = String(r.result || ''); resolve(u.slice(u.indexOf(',') + 1)); };
    r.onerror = () => reject(r.error || new Error('파일을 읽지 못했습니다.'));
    r.readAsDataURL(b);
  });
}

async function fetchVideoBlob(url) {
  // S3 는 Origin 이 붙은 요청에만 CORS 헤더를 준다 — 캐시를 건너뛰어 새로 받는다 (introVideoBuilder.loadImage 와 같은 이유)
  const res = await fetch(url, { mode: 'cors', cache: 'reload' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
}

export default function MockupVideoModal({ open, onClose, productId, contentNo, source, attachments = [], onAttached }) {
  const [blob, setBlob] = useState(null);
  const [blobUrl, setBlobUrl] = useState('');
  const [meta, setMeta] = useState(null); // { duration, width, height }
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [aspect, setAspect] = useState('16:9');
  const [tier, setTier] = useState('fast');
  const [audioMode, setAudioMode] = useState('mockup');
  const [maxScenes, setMaxScenes] = useState(6);
  const [notes, setNotes] = useState('');
  const [editInstruction, setEditInstruction] = useState(''); // 첫 화면 수정 요청 (분석용 요청사항과 따로 — 분석 지시가 그림 수정 지시로 가지 않게)
  const [refs, setRefs] = useState([]); // 참고 사진 [{ id, name, url(미리보기 — 내 파일이면 blob:, 첨부 이미지면 S3 주소), data(base64 jpeg) }]
  const [refsLoading, setRefsLoading] = useState(false);
  const [refDragOver, setRefDragOver] = useState(false);
  const [plan, setPlan] = useState(null); // { summary, scenes:[{ start, end, mode, firstFrame, overlayText, description, descriptionKo, thumb, image, status, clip, error, elapsed }] }
  const [busy, setBusy] = useState(''); // '' | 'analyze' | 'generate' | 'concat' | 'upload'
  const [progress, setProgress] = useState('');
  const [result, setResult] = useState(null); // { blob, url, durationSec }
  const [omni, setOmni] = useState(null); // Omni 결과 { blob, url } — 마무리(assemble) 전 원본
  const [omniTick, setOmniTick] = useState(null); // { elapsedSec, state }
  const [attached, setAttached] = useState(null);
  const abortRef = useRef(null);
  const fileInputRef = useRef(null);
  const refInputRef = useRef(null);
  const clipUrlsRef = useRef([]);
  const refsRef = useRef(refs);
  refsRef.current = refs;
  const mountedRef = useRef(true);
  // 붙을 때 true 로 다시 세운다 — 개발 모드(StrictMode)는 붙였다 뗐다 다시 붙이므로, 떼는 쪽만 있으면 영원히 false 로 남는다
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  // 클립 캐시 키에 쓰는 목업 식별자 — 직접 고른 파일이면 그 파일 기준
  const sourceKey = blob instanceof File ? `${blob.name}:${blob.size}:${blob.lastModified}` : `${source?.파일경로 || source?.파일명 || ''}:${blob?.size || 0}`;

  // 열릴 때 목업 mp4 를 받아 둔다
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setPlan(null); setResult(null); setAttached(null); setError(''); setLoadError(''); setBlob(null); setMeta(null); setOmni(null);
    resetUsageLog();
    if (source?.파일경로) {
      fetchVideoBlob(source.파일경로)
        .then((b) => { if (!cancelled) setBlob(b); })
        .catch((e) => { if (!cancelled) setLoadError(`첨부 영상을 불러오지 못했습니다 (${e.message}). 같은 파일을 아래에서 직접 선택해 주세요.`); });
    }
    return () => { cancelled = true; abortRef.current?.abort(); };
  }, [open, source]);

  // 받은 영상의 길이·비율을 읽는다
  useEffect(() => {
    setMeta(null);
    if (!blob) { setBlobUrl(''); return undefined; }
    let cancelled = false;
    const url = URL.createObjectURL(blob);
    setBlobUrl(url);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => {
      if (cancelled) return;
      const m = { duration: v.duration, width: v.videoWidth, height: v.videoHeight };
      setMeta(m);
      setAspect(m.height > m.width ? '9:16' : '16:9');
    };
    v.onerror = () => { if (!cancelled) setLoadError('이 영상은 브라우저가 열 수 없는 형식입니다. mp4(H.264)로 다시 저장해 주세요.'); };
    v.src = url;
    return () => { cancelled = true; v.removeAttribute('src'); URL.revokeObjectURL(url); };
  }, [blob]);

  useEffect(() => () => { if (result?.url) URL.revokeObjectURL(result.url); }, [result]);

  const tooLong = meta && meta.duration > MAX_MOCKUP_SEC + 0.5;
  // 10초 이하 목업은 Omni 로 영상을 직접 넣어 만든다. 넘으면 장면을 나눠 Veo 로
  const engine = meta && meta.duration <= OMNI_MAX_INPUT_SEC + 0.05 ? 'omni' : 'veo';
  const omniUsd = meta ? meta.duration * OMNI_USD_PER_SEC_ESTIMATE : 0;
  const scenes = plan?.scenes || [];
  const pending = scenes.filter((s) => s.status !== 'done');
  // 렌더링 사진 모드에서 아직 첫 화면이 없는 장면은 영상 전에 첫 화면을 만든다 (그 비용도 포함)
  const pendingCompose = plan?.refMode ? pending.filter((s) => !s.edited && s.image).length : 0;
  const estimateUsd = pending.reduce((n, s) => n + veoSecondsFor(s.end - s.start) * veoUsdPerSec(tier), 0) + pendingCompose * IMAGE_USD_PER_IMAGE;
  const allDone = scenes.length > 0 && pending.length === 0;
  const totalSec = scenes.reduce((n, s) => n + (s.end - s.start), 0);
  const refMode = !!plan?.refMode;
  // 렌더링 사진 모드는 사진이 있어야 첫 화면을 만들 수 있다. 아니면 수정 요청이나 사진 중 하나
  const canEdit = refMode ? refs.length > 0 : (!!editInstruction.trim() || refs.length > 0);
  const refsChanged = refMode && (plan.refIds || []).join('|') !== refs.map((r) => r.id).join('|');
  /** 장면이 그릴 렌더링 사진 — 분석이 고른 번호(refs)를 지금 사진 목록에서 찾는다. 못 찾으면(사진을 바꿨으면) 전부 */
  const sceneRefsFor = (sc) => {
    if (!refMode) return refs;
    const ids = (sc.refs || []).map((k) => plan.refIds?.[k]).filter(Boolean);
    const found = refs.filter((r) => ids.includes(r.id));
    return found.length ? found : refs;
  };
  const uneditedCount = scenes.filter((s) => !s.edited && s.image).length;
  const usage = summarizeUsage(usageLog); // 기록이 몇 줄뿐이라 렌더마다 합산해도 가볍다

  const updateScene = (i, patch) => setPlan((p) => ({ ...p, scenes: p.scenes.map((s, j) => (j === i ? { ...s, ...patch } : s)) }));

  const analyze = async () => {
    if (!blob || !meta) return;
    setError(''); setBusy('analyze'); setResult(null); setAttached(null);
    const ctrl = new AbortController(); abortRef.current = ctrl;
    try {
      const d = meta.duration;
      const n = Math.min(ANALYZE_FRAMES, Math.max(4, Math.ceil(d)));
      setProgress('목업에서 프레임을 뽑는 중…');
      const { frames } = await grabFrames(blob, Array.from({ length: n }, (_, i) => ((i + 0.5) * d) / n));
      // 렌더링 사진이 있으면 같이 보낸다 — 목업은 카메라 안내, 사진은 그릴 내용으로 나눠 분석한다
      const refSnapshot = refs.slice();
      setProgress(refSnapshot.length ? `AI 가 목업(카메라)과 사진 ${refSnapshot.length}장(내용)을 나눠 분석하는 중… (10~40초)` : 'AI 가 장면을 나누는 중… (10~30초)');
      const p = await requestMockupPlan({
        frames: frames.map((f) => ({ t: Math.round(f.t * 10) / 10, data: f.thumb })), durationSec: d, aspectRatio: aspect, maxScenes, notes,
        references: refSnapshot.map((r) => ({ data: r.data, mimeType: 'image/jpeg' })), signal: ctrl.signal,
      });
      // 장면마다 시작 화면(첫 프레임용 고해상도 + 표시용 썸네일)
      setProgress('장면 시작 화면을 준비하는 중…');
      const starts = await grabFrames(blob, p.scenes.map((s) => Math.min(s.start + 0.15, d - 0.05)), { thumbSize: 320, aspect });
      // 분석 요청사항을 첫 화면 수정 요청의 초안으로 (비어 있을 때만 — 사용자가 확인하고 고친다)
      if (!p.refMode && !editInstruction.trim() && notes.trim()) setEditInstruction(notes.trim());
      setPlan({
        summary: p.summary,
        refMode: !!p.refMode,
        refIds: p.refMode ? refSnapshot.map((r) => r.id) : [],
        scenes: p.scenes.map((s, i) => ({ ...s, thumb: starts.frames[i]?.thumb, image: starts.frames[i]?.firstFrame, status: 'idle' })),
      });
    } catch (e) {
      if (e?.name !== 'AbortError') setError(e?.message || String(e));
    } finally {
      setBusy(''); setProgress('');
    }
  };

  const generate = async (only) => {
    const targets = scenes.map((s, i) => ({ s, i })).filter(({ s, i }) => (only == null ? s.status !== 'done' : i === only));
    if (!targets.length) return;
    // 렌더링 사진 모드에서 첫 화면이 아직 없는 장면은 영상 전에 첫 화면부터 만든다
    const needsCompose = (sc) => refMode && !sc.edited && sc.image && refs.length > 0;
    if (refMode && !refs.length && targets.some(({ s }) => !s.edited)) {
      setError('렌더링 사진이 모두 빠졌습니다. 사진을 다시 넣거나(사진 없이 만들려면 다시 분석) 진행해 주세요.');
      return;
    }
    const composeCount = targets.filter(({ s }) => needsCompose(s)).length;
    const cost = targets.reduce((n, { s }) => n + veoSecondsFor(s.end - s.start) * veoUsdPerSec(tier), 0) + composeCount * IMAGE_USD_PER_IMAGE;
    if (!window.confirm(`AI 영상 클립 ${targets.length}개를 만듭니다.${composeCount ? `\n(먼저 렌더링 사진으로 첫 화면 ${composeCount}장을 만듭니다)` : ''}\n예상 비용 약 $${cost.toFixed(2)} (Veo ${tier}, 실패한 클립은 과금되지 않음)\n클립 하나에 1~3분 걸립니다. 진행할까요?`)) return;
    setError(''); setBusy('generate'); setResult(null); setAttached(null);
    const ctrl = new AbortController(); abortRef.current = ctrl;
    try {
      for (const target of targets) {
        const { i } = target;
        let s = target.s;
        if (ctrl.signal.aborted) break;
        updateScene(i, { status: 'working', error: '', elapsed: 0 });
        try {
          if (needsCompose(s)) {
            updateScene(i, { editStatus: 'working', editError: '' });
            const patch = editedPatch(s, await buildFirstFrame(s, ctrl.signal));
            s = { ...s, ...patch };
            updateScene(i, { ...patch, status: 'working' });
          }
          const durationSeconds = veoSecondsFor(s.end - s.start);
          const frame = frameOf(s);
          const useImage = s.firstFrame && s.mode !== 'story' && frame;
          const mode = useImage ? s.mode : 'story';
          const imageKey = `${sourceKey}@${s.start}${s.useEdited && s.edited ? `#${fingerprint(s.edited)}` : ''}`;
          const key = clipCacheKey({ tier, aspectRatio: aspect, durationSeconds, mode, imageUrl: useImage ? imageKey : '', description: s.description });
          let clip = only == null ? await getCachedClip(key) : null; // "다시 만들기" 는 캐시를 쓰지 않는다
          if (!clip) {
            const r = await generateClip({
              description: s.description, mode, aspectRatio: aspect, durationSeconds, tier, signal: ctrl.signal,
              ...(useImage ? { image: { data: frame, mimeType: 'image/jpeg' } } : {}),
              onTick: ({ elapsedSec }) => updateScene(i, { elapsed: elapsedSec }),
            });
            clip = { blob: r.blob, seconds: r.durationSeconds };
            putCachedClip(key, r.blob, { seconds: r.durationSeconds });
          }
          const url = URL.createObjectURL(clip.blob);
          clipUrlsRef.current.push(url);
          if (s.clip?.url) URL.revokeObjectURL(s.clip.url);
          updateScene(i, { status: 'done', clip: { blob: clip.blob, url, seconds: clip.seconds } });
        } catch (e) {
          if (e?.name === 'AbortError') { updateScene(i, { status: 'idle', editStatus: '' }); break; }
          updateScene(i, { status: 'error', error: e?.message || String(e), editStatus: '' });
        }
      }
    } finally {
      setBusy('');
    }
  };

  /** 참고 사진 추가 — AI 에 보낼 크기(긴 변 1024px JPEG)로 줄여 둔다 */
  const revokeRef = (r) => { if (r.url.startsWith('blob:')) URL.revokeObjectURL(r.url); };
  const pushRefs = (added) => {
    if (!mountedRef.current) { added.forEach(revokeRef); return; }
    if (!added.length) return;
    setRefs((prev) => {
      const fresh = added.filter((a) => !prev.some((r) => r.id === a.id));
      added.filter((a) => !fresh.includes(a)).forEach(revokeRef);
      const next = [...prev, ...fresh];
      next.slice(MAX_REFERENCES).forEach(revokeRef);
      return next.slice(0, MAX_REFERENCES);
    });
  };
  const addRefs = async (files) => {
    const all = Array.from(files || []);
    const room = MAX_REFERENCES - refs.length;
    const images = all.filter((f) => f.type.startsWith('image/') || IMAGE_EXT_RE.test(f.name));
    const picked = images.slice(0, Math.max(0, room));
    const problems = [];
    if (all.length > images.length) problems.push(`사진이 아닌 파일은 뺐습니다: ${all.filter((f) => !images.includes(f)).map((f) => f.name).join(', ')}`);
    if (images.length > picked.length) problems.push(`참고 사진은 최대 ${MAX_REFERENCES}장입니다.`);
    setRefsLoading(true);
    const added = [];
    try {
      for (const f of picked) {
        const data = await fileToJpegBase64(f, 1024).catch(() => null);
        if (data) added.push({ id: `${f.name}:${f.size}:${f.lastModified}`, name: f.name, url: URL.createObjectURL(f), data });
        else problems.push(`열 수 없는 사진입니다 (JPG·PNG 로 다시 저장해 주세요): ${f.name}`);
      }
    } finally {
      // 무슨 일이 있어도 로딩 표시는 끈다
      if (mountedRef.current) { setRefsLoading(false); setError(problems.join(' ')); }
      pushRefs(added);
    }
  };
  /** 콘텐츠에 이미 첨부한 이미지를 참고 사진으로 */
  const addAttachmentRef = async (file) => {
    if (refs.length >= MAX_REFERENCES) { setError(`참고 사진은 최대 ${MAX_REFERENCES}장입니다.`); return; }
    setRefsLoading(true);
    let data = null;
    try {
      // S3 응답이 멈추면 무한 대기하지 않게 20초 상한
      data = await Promise.race([
        makeThumbnailBase64(file.파일경로, null, 1024),
        new Promise((resolve) => setTimeout(() => resolve(null), 20000)),
      ]);
    } catch { data = null; } finally {
      if (mountedRef.current) setRefsLoading(false);
    }
    if (!mountedRef.current) return;
    if (!data) { setError(`첨부 이미지를 불러오지 못했습니다: ${file.파일명}. 내 컴퓨터에서 직접 추가해 주세요.`); return; }
    setError('');
    pushRefs([{ id: file.파일경로, name: file.파일명, url: file.파일경로, data }]);
  };
  const removeRef = (id) => setRefs((prev) => prev.filter((r) => { if (r.id === id) revokeRef(r); return r.id !== id; }));
  const imageAttachments = (attachments || []).filter((f) => f?.파일경로 && IMAGE_EXT_RE.test(f.파일명 || f.파일경로));
  useEffect(() => () => refsRef.current.forEach((r) => { if (r.url.startsWith('blob:')) URL.revokeObjectURL(r.url); }), []);

  /**
   * 장면 첫 화면을 만든다. 렌더링 사진 모드면 "그 장면의 사진 내용을 목업 구도로"(compose), 아니면 목업 화면을 요청대로 고친다(edit).
   * @returns {Promise<string>} Veo 첫 프레임용 JPEG base64
   */
  const buildFirstFrame = async (sc, signal) => {
    const out = await editImage({
      image: { data: sc.image, mimeType: 'image/jpeg' },
      instruction: editInstruction.trim(),
      references: sceneRefsFor(sc).map((r) => ({ data: r.data, mimeType: 'image/jpeg' })),
      description: sc.description,
      aspectRatio: aspect,
      role: refMode ? 'compose' : 'edit',
      signal,
    });
    // 모델이 주는 PNG·크기 그대로면 Veo 상한(2MB)을 넘을 수 있다 — 출력 비율의 JPEG 로 다시 맞춘다
    const edited = await makeFirstFrameBase64(`data:${out.mimeType};base64,${out.data}`, null, aspect);
    if (!edited) throw new Error('만든 첫 화면을 읽지 못했습니다.');
    return edited;
  };
  /**
   * 첫 화면을 얻은 장면의 바뀔 값. 새 첫 화면은 실사라 첫 프레임으로 쓸 수 있다 — "새로 그림" 이던 장면도 그 화면에서 출발하게 바꾼다.
   * 원래 설정(origFirstFrame/origMode)은 "첫 화면 사용" 을 끄면 되돌리려고 처음 한 번만 적어 둔다.
   */
  const editedPatch = (sc, edited) => ({
    edited, useEdited: true, editStatus: '', firstFrame: true,
    origFirstFrame: sc.origFirstFrame ?? sc.firstFrame, origMode: sc.origMode ?? sc.mode,
    mode: sc.mode === 'story' ? (sc.origMode && sc.origMode !== 'story' ? sc.origMode : 'action') : sc.mode,
    status: sc.status === 'done' ? 'stale' : sc.status,
  });

  /** 장면 첫 화면 만들기/고치기 (only 가 없으면 아직 없는 장면 전부) */
  const editFrames = async (only) => {
    const targets = scenes.map((s, i) => ({ s, i })).filter(({ s, i }) => s.image && (only == null ? !s.edited : i === only));
    if (!targets.length || !canEdit) return;
    setError(''); setBusy('edit'); setResult(null); setAttached(null);
    const ctrl = new AbortController(); abortRef.current = ctrl;
    try {
      for (const { s, i } of targets) {
        if (ctrl.signal.aborted) break;
        updateScene(i, { editStatus: 'working', editError: '' });
        try {
          updateScene(i, editedPatch(s, await buildFirstFrame(s, ctrl.signal)));
        } catch (e) {
          if (e?.name === 'AbortError') { updateScene(i, { editStatus: '' }); break; }
          updateScene(i, { editStatus: 'error', editError: e?.message || String(e) });
        }
      }
    } finally {
      setBusy('');
    }
  };

  /** 10초 이하 목업 → Omni 로 한 번에 */
  const runOmni = async () => {
    if (!blob || !meta) return;
    if (!window.confirm(`목업 영상(${meta.duration.toFixed(1)}초)을 AI(Gemini Omni)로 실사 영상으로 바꿉니다.\n렌더링 사진 ${refs.length}장 사용.\n예상 비용 약 $${omniUsd.toFixed(2)} (추정 단가 — 실제 청구액과 다를 수 있음)\n보통 1~3분 걸립니다. 진행할까요?`)) return;
    setError(''); setBusy('omni'); setResult(null); setAttached(null); setOmniTick(null);
    const ctrl = new AbortController(); abortRef.current = ctrl;
    try {
      // 목업을 AI 에 보낼 크기로 줄인다 (출력 비율로 맞추고 소리는 뺀다 — 소리는 마무리에서 고른다)
      // 요청 본문 4MB 안에 사진과 목업이 같이 들어가야 한다 — 사진 몫을 빼고 남는 만큼만 목업에 준다
      const refChars = refs.reduce((n, r) => n + r.data.length, 0);
      const maxVideoBytes = Math.min(OMNI_UPLOAD_MAX_BYTES, Math.floor(((OMNI_BODY_BUDGET_CHARS - refChars) * 3) / 4));
      if (maxVideoBytes < 400_000) throw new Error('렌더링 사진이 너무 커서 목업을 보낼 자리가 없습니다. 사진 수를 줄여 주세요.');
      // Omni 편집 입력은 10초 이하 — 컨테이너 길이가 10.0x 초여도 넘지 않게 자른다
      const seconds = Math.min(meta.duration, OMNI_MAX_INPUT_SEC - 1 / 30);
      setProgress('목업을 AI 전송용으로 줄이는 중…');
      let bitrate = OMNI_UPLOAD_BITRATE;
      let small = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        small = await concatClips([{ blob, seconds }], { aspect, audio: 'none', videoBitrate: bitrate, signal: ctrl.signal });
        if (small.blob.size <= maxVideoBytes) break;
        // 실제로 넘친 비율만큼 낮춘다 (여유 0.9)
        bitrate = Math.max(300_000, Math.floor(bitrate * (maxVideoBytes / small.blob.size) * 0.9));
      }
      if (small.blob.size > maxVideoBytes) throw new Error('목업을 충분히 줄이지 못했습니다. 더 짧게 자르거나 렌더링 사진 수를 줄여 주세요.');
      setProgress('AI 가 영상을 만드는 중…');
      const out = await generateOmni({
        video: { data: await blobToBase64(small.blob) },
        references: refs.map((r) => ({ data: r.data, mimeType: 'image/jpeg' })),
        instruction: notes.trim(),
        aspectRatio: aspect,
        seconds,
        signal: ctrl.signal,
        onTick: (t) => setOmniTick(t),
      });
      if (ctrl.signal.aborted || !mountedRef.current) return; // 그사이 닫혔으면 버린다
      const url = URL.createObjectURL(out.blob);
      clipUrlsRef.current.push(url);
      setOmni({ blob: out.blob, url });
    } catch (e) {
      if (e?.name !== 'AbortError') setError(e?.message || String(e));
    } finally {
      setBusy(''); setProgress(''); setOmniTick(null);
    }
  };

  const assemble = async () => {
    setError(''); setBusy('concat'); setAttached(null);
    const ctrl = new AbortController(); abortRef.current = ctrl;
    try {
      const segments = engine === 'omni'
        ? [{ blob: omni.blob, seconds: meta.duration, srcStart: 0 }] // 목업 길이로 맞춘다 (결과가 더 짧으면 그 길이)
        : scenes.map((s) => ({ blob: s.clip.blob, seconds: Math.min(s.end - s.start, s.clip.seconds || 8), srcStart: s.start }));
      const out = await concatClips(
        segments,
        { aspect, audio: audioMode, audioBlob: blob, signal: ctrl.signal, onProgress: ({ done, total }) => setProgress(`영상 합치는 중… ${Math.round((done / total) * 100)}%`) },
      );
      if (out.blob.size > MAX_OUTPUT_BYTES) throw new Error(`완성 영상이 ${(out.blob.size / 1048576).toFixed(1)}MB 로 업로드 상한(20MB)을 넘습니다. 장면 수를 줄여 주세요.`);
      setResult({ blob: out.blob, url: URL.createObjectURL(out.blob), durationSec: out.durationSec });
    } catch (e) {
      if (e?.name !== 'AbortError') setError(e?.message || String(e));
    } finally {
      setBusy(''); setProgress('');
    }
  };

  const attach = async () => {
    if (!result || !contentNo) return;
    setError(''); setBusy('upload');
    try {
      const base = String(source?.파일명 || 'mockup').replace(/\.[^.]+$/, '').replace(/_\d{8}_\d{6}$/, '');
      const file = new File([result.blob], `ai_${base}.mp4`, { type: 'video/mp4' });
      const up = await uploadContentFile({ file, productNo: productId, contentNo });
      if (!up || up.error) throw new Error(up?.error || '업로드 실패');
      const entry = { 파일번호: Date.now(), 파일명: up.fileName || file.name, 파일경로: `https://kr.object.iwinv.kr/orgin/1/${up.filePath}` };
      setAttached(entry);
      onAttached?.(entry);
    } catch (e) {
      setError(`첨부하지 못했습니다: ${e?.message || e}`);
    } finally {
      setBusy('');
    }
  };

  // 클립 미리보기 URL 은 모달이 사라질 때 한꺼번에 정리한다 (장면이 바뀔 때마다 지우면 화면의 <video> 가 깨진다)
  useEffect(() => () => { clipUrlsRef.current.forEach((u) => URL.revokeObjectURL(u)); clipUrlsRef.current = []; }, []);

  if (!open) return null;
  const step = attached ? 4 : result ? 3 : (engine === 'omni' ? omni : allDone) ? 3 : (engine === 'omni' ? 2 : plan ? 2 : 1);

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[94vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-violet-100 text-violet-700 text-xs font-bold">AI</span>
            목업으로 동영상 만들기
            <span className="text-xs font-normal text-gray-500 truncate max-w-[240px]" title={source?.파일명}>— {source?.파일명}</span>
          </h3>
          <div className="flex items-center gap-2">
            {engine === 'veo' && <StepChip n={1} label="분석" active={step === 1} done={step > 1} />}
            <StepChip n={2} label="생성" active={step === 2} done={step > 2} />
            <StepChip n={3} label={engine === 'omni' ? '마무리' : '합치기'} active={step === 3} done={step > 3} />
            <StepChip n={4} label="첨부" active={step === 4} done={false} />
            <button type="button" className="ml-2 text-gray-400 hover:text-gray-700" onClick={() => { abortRef.current?.abort(); onClose?.(); }} title="닫기">✕</button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {!isAiConfigured() && <Notice tone="warn">AI 연결 설정이 없습니다. .env 의 REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_ANON_KEY / REACT_APP_ERP_APP_TOKEN 을 확인하세요.</Notice>}
          {!isVideoBuildSupported() && <Notice tone="warn">이 브라우저는 영상 합치기(WebCodecs)를 지원하지 않습니다. 최신 Chrome 또는 Edge 에서 열어 주세요.</Notice>}
          {loadError && <Notice tone="error">{loadError}</Notice>}
          {tooLong && <Notice tone="error">목업 영상이 {meta.duration.toFixed(0)}초입니다. {MAX_MOCKUP_SEC}초 이내로 잘라서 다시 첨부해 주세요.</Notice>}
          {error && <Notice tone="error">{error}</Notice>}

          <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4">
            {/* 목업 */}
            <div>
              <div className="text-xs font-semibold text-gray-700 mb-1">목업 영상 {meta && <span className="font-normal text-gray-500">· {meta.duration.toFixed(1)}초 · {meta.width}×{meta.height}</span>}</div>
              {blobUrl ? (
                <video src={blobUrl} controls muted playsInline className="w-full max-h-[260px] bg-black rounded-lg" />
              ) : !loadError ? (
                <div className="h-[180px] rounded-lg bg-gray-100 flex items-center justify-center text-xs text-gray-500"><Spinner />불러오는 중…</div>
              ) : null}
              <button type="button" className="mt-1 text-[11px] text-gray-500 hover:text-violet-700 underline" onClick={() => fileInputRef.current?.click()} disabled={!!busy}>내 컴퓨터에서 목업 파일 선택</button>
              <input ref={fileInputRef} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) { setLoadError(''); setPlan(null); setOmni(null); setResult(null); setAttached(null); setBlob(f); } e.target.value = ''; }} />
            </div>

            {/* 옵션 */}
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <Field label="화면 비율">
                  <select className="w-full border rounded px-2 py-1 text-sm" value={aspect} onChange={(e) => { setAspect(e.target.value); setOmni(null); setResult(null); setAttached(null); }} disabled={!!plan || !!busy}>
                    <option value="16:9">가로 16:9</option>
                    <option value="9:16">세로 9:16</option>
                  </select>
                </Field>
                {engine === 'veo' && (
                  <Field label="최대 장면 수">
                    <select className="w-full border rounded px-2 py-1 text-sm" value={maxScenes} onChange={(e) => setMaxScenes(Number(e.target.value))} disabled={!!busy}>
                      {[3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n}개</option>)}
                    </select>
                  </Field>
                )}
                {engine === 'veo' && (
                  <Field label="AI 영상 품질 (비용)">
                    <select className="w-full border rounded px-2 py-1 text-sm" value={tier} onChange={(e) => setTier(e.target.value)} disabled={!!busy}>
                      {VEO_TIERS.map((t) => <option key={t.id} value={t.id}>{t.label} · ${t.usdPerSec}/초</option>)}
                    </select>
                  </Field>
                )}
                <Field label="소리">
                  <select className="w-full border rounded px-2 py-1 text-sm" value={audioMode} onChange={(e) => { setAudioMode(e.target.value); setResult(null); setAttached(null); }} disabled={!!busy}>
                    {AUDIO_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                </Field>
              </div>
              <Field label={engine === 'omni' ? '추가 요청 (선택)' : '요청사항 (선택)'}>
                <textarea className="w-full border rounded px-2 py-1 text-sm h-16 resize-none" maxLength={500} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!!busy}
                  placeholder={engine === 'omni' ? '예) 해질녘 분위기, 앞쪽에 30대 한국인 부부 여행객' : '예) 장소는 바르셀로나, 인물은 30대 한국인 부부, 템포는 느리게'} />
              </Field>
              {/* 참고 사진 — <label>(Field) 로 감싸지 않는다: label 안에 버튼이 있으면 빈 곳을 눌러도 첫 버튼(✕ 빼기)이 눌린다 */}
              <div
                className={`rounded-lg border px-2 py-1.5 ${refDragOver ? 'border-violet-500 bg-violet-50' : 'border-gray-200'}`}
                onDragOver={(e) => { e.preventDefault(); setRefDragOver(true); }}
                onDragLeave={() => setRefDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setRefDragOver(false); if (!busy) addRefs(e.dataTransfer.files); }}
              >
                <div className="flex items-center justify-between text-[11px] mb-1">
                  <span className="font-medium text-gray-600">렌더링 사진 — 화면에 그릴 내용 (최대 {MAX_REFERENCES}장)</span>
                  <span className={refs.length ? 'text-green-700 font-medium' : 'text-gray-400'}>
                    {refsLoading ? <><Spinner />사진 준비 중…</> : refs.length ? `✓ ${refs.length}장 첨부됨` : '없음'}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {refs.map((r) => (
                    <div key={r.id} className="relative w-20 h-14 rounded overflow-hidden border border-green-400">
                      <img src={r.url} alt={r.name} title={r.name} className="w-full h-full object-cover" />
                      <button type="button" className="absolute top-0 right-0 w-5 h-5 text-[11px] leading-5 bg-black/60 text-white" onClick={() => removeRef(r.id)} disabled={!!busy} title="빼기">✕</button>
                    </div>
                  ))}
                  {refs.length < MAX_REFERENCES && (
                    <button type="button" className="w-20 h-14 rounded border border-dashed text-[11px] text-gray-500 hover:text-violet-700 hover:border-violet-400 disabled:opacity-50" onClick={() => refInputRef.current?.click()} disabled={!!busy || refsLoading} title="참고 사진 추가 (예: 사그라다 파밀리아)">
                      + 내 컴퓨터
                    </button>
                  )}
                  <input ref={refInputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { const files = Array.from(e.target.files || []); e.target.value = ''; addRefs(files); }} />
                </div>
                {refs.length > 0 && <div className="mt-1 text-[10px] text-gray-500 truncate">{refs.map((r) => r.name).join(' · ')}</div>}
                {imageAttachments.length > 0 && refs.length < MAX_REFERENCES && (
                  <div className="mt-1.5 text-[11px] text-gray-600">
                    콘텐츠 첨부 이미지에서 고르기:
                    <div className="mt-1 flex gap-1.5 flex-wrap">
                      {imageAttachments.map((f) => {
                        const used = refs.some((r) => r.id === f.파일경로);
                        return (
                          <button key={f.파일경로} type="button" className={`w-14 h-10 rounded overflow-hidden border ${used ? 'border-green-500 opacity-40' : 'hover:border-violet-500'}`}
                            onClick={() => addAttachmentRef(f)} disabled={!!busy || refsLoading || used} title={used ? `${f.파일명} (추가됨)` : f.파일명}>
                            <img src={f.파일경로} alt={f.파일명} className="w-full h-full object-cover" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="mt-1 text-[10px] text-gray-400">
                  {engine === 'omni' ? '목업은 카메라·구도·타이밍 안내로만 쓰고, 화면 내용은 이 사진으로 그립니다.' : '분석 전에 넣으면 목업은 카메라·구도 안내로만 쓰고, 화면 내용은 이 사진으로 그립니다.'} 끌어다 놓아도 됩니다.
                </div>
                {refsChanged && <div className="mt-1 text-[11px] text-amber-700">분석 뒤에 사진이 바뀌었습니다 — 장면별 사진 배정을 맞추려면 다시 분석하세요.</div>}
              </div>
              {engine === 'omni' ? (
                <>
                  <button type="button" className="w-full py-2 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
                    onClick={runOmni} disabled={!blob || !meta || !!loadError || !!busy || refsLoading || !isVideoBuildSupported()}>
                    {busy === 'omni'
                      ? <><Spinner />{omniTick ? `AI 가 영상을 만드는 중… ${omniTick.elapsedSec}초` : progress}</>
                      : omni ? `AI 영상 다시 만들기 (약 $${omniUsd.toFixed(2)})` : `AI 영상 만들기 (약 $${omniUsd.toFixed(2)})`}
                  </button>
                  {busy === 'omni' && <button type="button" className="w-full py-1 rounded-lg border text-xs" onClick={() => abortRef.current?.abort()}>중단</button>}
                  <p className="text-[11px] text-gray-500 leading-relaxed">
                    10초 이하 목업이라 <b className="text-violet-700">목업 영상을 AI(Gemini Omni)에 그대로 넣어</b> 만듭니다.
                    {' '}목업 = 카메라 움직임·구도·타이밍{refs.length ? ` / 사진 ${refs.length}장 = 화면 내용` : ' (렌더링 사진을 넣으면 화면 내용을 그 사진으로 정합니다)'}.
                    {' '}비용은 추정치입니다.
                  </p>
                </>
              ) : (
              <button type="button" className="w-full py-2 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
                onClick={analyze} disabled={!blob || !meta || !!loadError || tooLong || !!busy}>
                {busy === 'analyze' ? <><Spinner />{progress}</> : plan ? '1. 다시 분석' : '1. 목업 분석 (장면 나누기)'}
              </button>
              )}
              {engine === 'veo' && <p className="text-[11px] text-gray-500 leading-relaxed">
                {refs.length ? (
                  <><b className="text-violet-700">목업 = 카메라·구도 안내 / 사진 {refs.length}장 = 렌더링 내용</b>으로 나눠 분석합니다. 장면마다 그 사진의 장면을 목업 구도로 그린 뒤 영상으로 만듭니다.</>
                ) : (
                  <>렌더링 사진이 없으면 목업 화면 자체를 바탕으로 만듭니다. 장면 내용을 사진으로 정하려면 분석 전에 위에 사진을 넣으세요.</>
                )}
                {' '}화면 위 자막·로고 글자는 AI 가 제대로 못 그려 넣지 않습니다.
              </p>}
            </div>
          </div>

          {/* Omni 결과 */}
          {engine === 'omni' && omni && (
            <div className="mt-4 border rounded-lg p-3">
              <div className="text-xs font-semibold text-gray-700 mb-1">AI 가 만든 영상 (마무리 전)</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[11px] text-gray-500 mb-0.5">목업</div>
                  <video src={blobUrl} controls muted playsInline className="w-full bg-black rounded" />
                </div>
                <div>
                  <div className="text-[11px] text-gray-500 mb-0.5">AI 결과</div>
                  <video src={omni.url} controls playsInline className="w-full bg-black rounded" />
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <button type="button" className="px-4 py-2 rounded-lg border border-violet-600 text-violet-700 text-sm font-medium hover:bg-violet-50 disabled:opacity-50"
                  onClick={assemble} disabled={!!busy || !isVideoBuildSupported()}>
                  {busy === 'concat' ? <><Spinner />{progress || '마무리 중…'}</> : '마무리 (소리·길이·크기 맞추기)'}
                </button>
                <span className="text-[11px] text-gray-500">소리: {AUDIO_OPTIONS.find((o) => o.id === audioMode)?.label}. 마무리하면 목업 길이·상세페이지용 크기(20MB 이하)로 맞춥니다.</span>
              </div>
            </div>
          )}

          {/* 장면 계획 */}
          {engine === 'veo' && plan && (
            <div className="mt-4">
              {plan.summary && <div className="mb-2 text-xs text-gray-600 bg-gray-50 border rounded-lg px-3 py-2">{plan.summary}</div>}
              <div className="mb-2 flex items-center gap-2 border border-violet-200 bg-violet-50/50 rounded-lg px-3 py-2">
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] text-gray-600 mb-1">
                    {refMode ? (
                      <><b>첫 화면 만들기</b> — 장면마다 렌더링 사진의 내용을 목업의 구도·앵글로 그립니다. 영상 만들기 전에 확인할 수 있고, 바로 영상 만들기를 눌러도 자동으로 먼저 만듭니다.</>
                    ) : (
                      <>배경·장소를 바꾸려면 영상을 만들기 전에 <b>첫 화면을 먼저 고치세요</b>. 구도·카메라 앵글은 그대로 두고 아래 요청과 사진대로 다시 그립니다.</>
                    )}
                  </div>
                  <input type="text" className="w-full border rounded px-2 py-1 text-xs bg-white" maxLength={500} value={editInstruction} disabled={!!busy}
                    onChange={(e) => setEditInstruction(e.target.value)}
                    placeholder={refMode ? '추가 요청 (선택) — 예) 해질녘 분위기, 앞쪽에 여행객 2명' : '첫 화면 수정 요청 — 예) 배경을 사그라다 파밀리아로 바꿔줘'} />
                  <div className="mt-1 flex items-center gap-1 text-[11px]">
                    {refs.length ? (
                      <>
                        <span className="text-green-700">{refMode ? '렌더링 사진' : '사진'} {refs.length}장 사용:</span>
                        {refs.map((r) => <img key={r.id} src={r.url} alt={r.name} title={r.name} className="w-8 h-6 object-cover rounded border" />)}
                      </>
                    ) : (
                      <span className="text-gray-400">사진 없음 — 위 "렌더링 사진"에서 추가할 수 있습니다</span>
                    )}
                  </div>
                </div>
                <button type="button" className="shrink-0 px-3 py-1.5 rounded-lg bg-white border border-violet-500 text-violet-700 text-xs font-medium hover:bg-violet-50 disabled:opacity-50"
                  onClick={() => editFrames()} disabled={!!busy || !canEdit || !uneditedCount}>
                  {busy === 'edit' ? <><Spinner />첫 화면 만드는 중…</> : uneditedCount ? `첫 화면 ${refMode ? '만들기' : '고치기'} (${uneditedCount}장, 약 $${(uneditedCount * IMAGE_USD_PER_IMAGE).toFixed(2)})` : '모든 첫 화면 완료'}
                </button>
                {busy === 'edit' && <button type="button" className="shrink-0 px-2 py-1.5 rounded-lg border text-xs" onClick={() => abortRef.current?.abort()}>중단</button>}
              </div>
              <div className="space-y-2">
                {scenes.map((s, i) => (
                  <div key={i} className="flex gap-3 border rounded-lg p-2">
                    <div className="w-40 shrink-0">
                      {s.clip ? (
                        <video src={s.clip.url} controls muted loop playsInline className="w-full rounded bg-black" />
                      ) : s.useEdited && s.edited ? (
                        <img src={`data:image/jpeg;base64,${s.edited}`} alt="" className="w-full rounded ring-2 ring-violet-400" />
                      ) : s.thumb ? (
                        <img src={`data:image/jpeg;base64,${s.thumb}`} alt="" className={`w-full rounded ${s.firstFrame ? '' : 'opacity-50'}`} />
                      ) : null}
                      <div className="mt-1 text-[11px] text-gray-500">장면 {i + 1} · {s.start.toFixed(1)}~{s.end.toFixed(1)}초 ({(s.end - s.start).toFixed(1)}초)</div>
                      {s.edited && (
                        <label className="mt-0.5 flex items-center gap-1 text-[11px] text-violet-700">
                          <input type="checkbox" checked={!!s.useEdited} disabled={!!busy}
                            onChange={(e) => {
                              const on = e.target.checked;
                              // 끄면 원래 설정으로 — 첫 프레임으로 못 쓴다던 목업 화면(스케치 등)에서 Veo 가 출발하지 않게
                              const restore = s.origFirstFrame === false ? { firstFrame: false, mode: 'story' } : {};
                              const reapply = s.origFirstFrame === false ? { firstFrame: true, mode: s.origMode && s.origMode !== 'story' ? s.origMode : 'action' } : {};
                              updateScene(i, { useEdited: on, ...(on ? reapply : restore), status: s.status === 'done' ? 'stale' : s.status });
                            }} />
                          {refMode ? '사진으로 만든 첫 화면 사용' : '고친 첫 화면 사용'}
                        </label>
                      )}
                      {s.editStatus === 'working' && <div className="text-[11px] text-violet-700"><Spinner />첫 화면 만드는 중…</div>}
                      {refMode && (
                        <div className="mt-0.5 flex items-center gap-1 text-[10px] text-gray-500">
                          그릴 사진:
                          {sceneRefsFor(s).map((r) => <img key={r.id} src={r.url} alt={r.name} title={r.name} className="w-7 h-5 object-cover rounded border border-green-400" />)}
                        </div>
                      )}
                      {s.editStatus === 'error' && <div className="text-[11px] text-red-600">{s.editError}</div>}
                      {s.image && (
                        <button type="button" className="text-[11px] text-violet-700 hover:underline disabled:opacity-50" onClick={() => editFrames(i)} disabled={!!busy || !canEdit}>
                          {refMode ? (s.edited ? '첫 화면 다시 만들기' : '이 장면 첫 화면 만들기') : (s.edited ? '첫 화면 다시 고치기' : '이 장면 첫 화면 고치기')}
                        </button>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2 text-[11px]">
                        {refMode ? (
                          // 렌더링 사진 모드: 목업 화면은 첫 프레임으로 쓰지 않으므로 움직임 종류만 고른다
                          <select className="border rounded px-1 py-0.5 disabled:opacity-50" value={s.mode === 'action' ? 'action' : 'photo'} disabled={!!busy || (!!s.edited && !s.useEdited)}
                            onChange={(e) => updateScene(i, { mode: e.target.value, origMode: e.target.value, status: s.status === 'done' ? 'stale' : s.status })}>
                            <option value="photo">{MODE_LABELS.photo}</option>
                            <option value="action">{MODE_LABELS.action}</option>
                          </select>
                        ) : (
                          <select className="border rounded px-1 py-0.5" value={s.firstFrame ? s.mode : 'story'} disabled={!!busy}
                            onChange={(e) => updateScene(i, { mode: e.target.value, firstFrame: e.target.value !== 'story', status: s.status === 'done' ? 'stale' : s.status })}>
                            {Object.entries(MODE_LABELS).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                          </select>
                        )}
                        <span className="text-gray-500">
                          {refMode
                            ? (s.useEdited && s.edited ? '사진으로 만든 첫 화면에서 출발' : s.edited ? '첫 화면 없이 설명으로 새로 그림' : '첫 화면을 사진 + 목업 구도로 만들 예정')
                            : (!s.firstFrame ? '첫 화면 없이 설명으로 새로 그림' : s.useEdited && s.edited ? '고친 화면을 첫 프레임으로 사용' : '목업 화면을 첫 프레임으로 사용')}
                        </span>
                        <span className="ml-auto text-gray-400">Veo {veoSecondsFor(s.end - s.start)}초</span>
                      </div>
                      {s.descriptionKo && <div className="text-xs text-gray-700">{s.descriptionKo}</div>}
                      <textarea className="w-full border rounded px-2 py-1 text-[11px] text-gray-600 h-14 resize-none font-mono" maxLength={300} value={s.description} disabled={!!busy}
                        onChange={(e) => updateScene(i, { description: e.target.value, status: s.status === 'done' ? 'stale' : s.status })} title="Veo 에 실제로 보내는 지시 (영어, 300자 이내)" />
                      {s.overlayText && <div className="text-[11px] text-amber-700">목업 자막(영상에는 안 들어감): {s.overlayText}</div>}
                      <div className="flex items-center gap-2 text-[11px]">
                        {s.status === 'working' && <span className="text-violet-700"><Spinner />생성 중… {s.elapsed || 0}초</span>}
                        {s.status === 'done' && <span className="text-green-700">✓ 완성</span>}
                        {s.status === 'stale' && <span className="text-amber-700">설정이 바뀌었습니다 — 다시 만들어야 반영됩니다</span>}
                        {s.status === 'error' && <span className="text-red-600">{s.error}</span>}
                        {(s.status === 'done' || s.status === 'error' || s.status === 'stale') && (
                          <button type="button" className="ml-auto text-violet-700 hover:underline disabled:opacity-50" onClick={() => generate(i)} disabled={!!busy}>이 장면 다시 만들기</button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-3 flex items-center gap-2">
                <button type="button" className="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
                  onClick={() => generate()} disabled={!!busy || !pending.length || (refMode && !refs.length && pending.some((s) => !s.edited))}
                  title={refMode && !refs.length ? '렌더링 사진을 다시 넣거나 다시 분석하세요' : undefined}>
                  {busy === 'generate' ? <><Spinner />AI 영상 만드는 중…</> : pending.length ? `2. 장면 ${pending.length}개 AI 영상 만들기 (약 $${estimateUsd.toFixed(2)})` : '2. 모든 장면 완성'}
                </button>
                {busy === 'generate' && <button type="button" className="px-3 py-2 rounded-lg border text-sm" onClick={() => abortRef.current?.abort()}>중단</button>}
                <button type="button" className="px-4 py-2 rounded-lg border border-violet-600 text-violet-700 text-sm font-medium hover:bg-violet-50 disabled:opacity-50"
                  onClick={assemble} disabled={!!busy || !allDone || !isVideoBuildSupported()}>
                  {busy === 'concat' ? <><Spinner />{progress || '합치는 중…'}</> : `3. 합치기 (${totalSec.toFixed(1)}초)`}
                </button>
                <span className="ml-auto text-[11px] text-gray-500">
                  이번에 쓴 비용 약 {usage.usd == null ? '-' : `$${usage.usd.toFixed(2)}`}{usage.veo.calls ? ` (Veo ${usage.veo.calls}개 ${usage.veo.seconds}초)` : ''}
                </span>
              </div>
            </div>
          )}

          {/* 결과 */}
          {result && (
            <div className="mt-4 border-t pt-3">
              <div className="text-xs font-semibold text-gray-700 mb-1">완성 영상 · {result.durationSec.toFixed(1)}초 · {(result.blob.size / 1048576).toFixed(1)}MB</div>
              <video src={result.url} controls playsInline className="w-full max-h-[360px] bg-black rounded-lg" />
              <div className="mt-2 flex items-center gap-2">
                <button type="button" className="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
                  onClick={attach} disabled={!!busy || !!attached || !contentNo}>
                  {busy === 'upload' ? <><Spinner />올리는 중…</> : attached ? '✓ 첨부됨' : '4. 이 콘텐츠에 첨부'}
                </button>
                <a href={result.url} download={`ai_${productId}_${aspect.replace(':', 'x')}.mp4`} className="px-3 py-2 rounded-lg border text-sm text-gray-700 hover:bg-gray-50">내려받기</a>
                {attached && <span className="text-xs text-green-700">첨부파일 목록에 추가했습니다. 편집 화면에서 저장을 눌러 주세요.</span>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
